import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Footer.css';
import { motion } from 'framer-motion';
import logocircular from '../../assets/svgs/logo circular1.png';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import DownloadModal from '../../components/Buttons/DownloadModal/DownloadModal.jsx';

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { translations: t } = useLanguage();
  const { currentUser, userProfile } = useAuth();

  // Actions dropdown
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsDropdownRef = useRef(null);

  // Auth handler - dispatch event to trigger mobile auth dropdown
  const toggleAuth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're on mobile (screen width < 900px)
    const isMobile = window.innerWidth < 900;
    
    if (isMobile && location.pathname === '/') {
      // On mobile and on authentication page → open mobile auth dropdown
      const event = new CustomEvent('openMobileAuth', {
        detail: { source: 'footer' },
        bubbles: true
      });
      document.dispatchEvent(event);
      console.log('[Footer] Dispatched openMobileAuth event');
    } else if (isMobile) {
      // On mobile but not on auth page → navigate first, then open dropdown
      navigate('/');
      setTimeout(() => {
        const event = new CustomEvent('openMobileAuth', {
          detail: { source: 'footer' },
          bubbles: true
        });
        document.dispatchEvent(event);
      }, 100);
    } else {
      // Desktop → trigger navbar auth dropdown
      const event = new CustomEvent('openNavbarAuth', {
        detail: { source: 'footer' },
        bubbles: true
      });
      document.dispatchEvent(event);
      console.log('[Footer] Dispatched openNavbarAuth event');
    }
  };

  // Profile click handler - redirect to feed
  const handleProfileClick = () => {
    console.log('[Footer] Profile clicked', { currentUser: !!currentUser, userProfile: !!userProfile });
    if (currentUser) {
      navigate('/feed');
    }
  };

  // Logo click handler - redirect to home/authentication page
  const handleLogoClick = () => {
    navigate('/');
  };

  // Close actions dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (isActionsOpen && actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target)) {
        setIsActionsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionsOpen]);

  // Toggle actions dropdown (commented out as not used)
  // const toggleActions = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setIsActionsOpen(prev => !prev);
  // };

  const footerLinks = [
    { title: t.footer?.links?.about || "About Us", url: "/about" },
    { title: t.footer?.links?.contact || "Contact Us", url: "/contact" },
    { title: t.footer?.links?.faq || "FAQ's", url: "/faq" },
    { title: t.footer?.links?.terms || "Terms Of Services", url: "/terms-of-services" },
    { title: t.footer?.links?.privacy || "Privacy Policy", url: "/privacy-policy" },
    {title: t.footer?.links?.investment || "Investment" ,url:"/investment"}
  ];

  return (
    <footer className={`footer ${isHomePage ? 'home-footer' : 'fullwidth-footer'}`}>
      <div className="footer-gradient"></div>
      <div className="footer-container">
        <div className="footer-top">
          <motion.div 
            className="footer-logo"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <img src={logocircular} alt="logo" className='footer-logo-img'/>
            <span className="logo-text">iWinto</span>
          </motion.div>
          
          <div className="footer-auth-section">
            <DownloadModal />
            
            {currentUser ? (
              // Show profile image when authenticated - click to go to feed
              <div className="footer-profile-toggle-container">
                <button
                  className="footer-profile-toggle"
                  onClick={handleProfileClick}
                  onMouseDown={(e) => e.preventDefault()}
                  aria-label="Go to Feed"
                >
                  <img
                    src={userProfile?.avatar_url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K"}
                    alt={userProfile?.display_name || currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || "Profile"}
                    className="footer-profile-image"
                  />
                </button>
              </div>
                         ) : (
               // Show sign in button when not authenticated
               <div className="footer-auth-wrapper">
                 <button
                   className="footer-sign-in-toggle"
                   onClick={toggleAuth}
                   onMouseDown={(e) => e.preventDefault()}
                 >
                   {t.navbar?.auth?.signIn || "Sign In"}
                 </button>
               </div>
             )}
          </div>
        </div>

        <div className="footer-middle">
          <div className="footer-links">
            {footerLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                className="footer-link"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.title}
              </motion.a>
            ))}
            
            {/* Actions Dropdown */}
            {/* <motion.div
              className="footer-actions-wrapper"
              ref={actionsDropdownRef}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <button
                className="footer-actions-toggle"
                onClick={toggleActions}
                onMouseDown={(e) => e.preventDefault()}
                aria-expanded={isActionsOpen}
                aria-controls="footer-actions-dropdown"
              >
                {t.navbar?.links?.actions || "Actions"}
              </button>

              <div
                id="footer-actions-dropdown"
                className={`footer-actions-dropdown ${isActionsOpen ? 'open' : ''}`}
              >
                <div className="footer-actions-list">
                  <button
                    onClick={() => {
                      setIsActionsOpen(false);
                      navigate('/partnership');
                    }}
                    className={`footer-action-item ${location.pathname === '/partnership' ? 'active' : ''}`}
                  >
                    {t.navbar?.links?.partnership || "Investment"}
                  </button>
                  <button
                    onClick={() => {
                      setIsActionsOpen(false);
                      navigate('/buy-winis');
                      // Scroll to plan section after navigation
                      setTimeout(() => {
                        const planSection = document.querySelector('.short-term-micro-plans-section');
                        if (planSection) {
                          planSection.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }
                      }, 100);
                    }}
                    className={`footer-action-item ${location.pathname === '/buy-winis' ? 'active' : ''}`}
                  >
                    {t.navbar?.links?.buyWinis || "Buy Winis"}
                  </button>
                </div>
              </div>
            </motion.div> */}
          </div>
        </div>

        <div className="footer-divider"></div>

        <div className="footer-bottom">
          <span className='copyright'>IWINTO LLC</span>
          <span className="copyright">{t.footer?.copyright || "© 2025 iWinto. All rights reserved."}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
